<template>
  <div v-loading="loading" class="enquete-tab">
    <div v-if="prizeId === null" class="message message-info">
      個別の応募グループを選択するとアンケートが表示されます。
    </div>
    <div v-else>
      <div v-if="!hasQuestions" class="message message-info">データがありません。</div>
      <div v-else>
        <div class="enquete-header">
          <el-select class="enquete-select" :value="selectedId" @change="handleQuestionChange" :disabled="loading">
            <el-option v-for="question in questions" :key="question.id" :label="question.text" :value="question.id" />
          </el-select>
          <el-tooltip placement="top" content="CSVダウンロード">
            <CsvDownload :download="downloadCsv" :disabled="!hasAnswers" text="" />
          </el-tooltip>
        </div>

        <div v-if="!hasAnswers" class="message message-info">データがありません。</div>
        <div v-else>
          <div class="enquete-body">
            <div class="enquete-chart">
              <AnalyticsChartDoughnut
                :data="chartData"
                :height="chartSize"
                :chartStyles="chartStyles"
                :show-legend="false"
                :colors="chartColors"
                ref="chart"
              />
            </div>
            <div class="enquete-legend">
              <div v-for="item in legendItems" :key="item.optionId" class="legend-item">
                <span class="legend-color" :style="{ backgroundColor: resolveLegendColor(item.optionId) }"></span>
                <span class="legend-text">{{ formatLegendLabel(item) }}</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed } from 'vue'

import AnalyticsChartDoughnut from '@/client/components/_atoms/analytics_chart_doughnut'
import CsvDownload from '@/client/components/_atoms/csv_download'

import { InstagramInstantwinEnqueteAnswer, InstagramInstantwinEnqueteQuestion } from '../types'

interface Props {
  prizeId: number | null
  questions: InstagramInstantwinEnqueteQuestion[]
  selectedId: number | null
  answers: InstagramInstantwinEnqueteAnswer[]
  totalCount: number
  campaignName: string
  loading: boolean
}

const props = defineProps<Props>()

const emit = defineEmits<{ (e: 'select-question', value: number): void }>()

const hasQuestions = computed(() => props.questions.length > 0)
const hasAnswers = computed(() => props.answers.length > 0)

const sortedAnswers = computed(() => {
  return props.answers
    .map((answer, index) => ({ answer, index }))
    .sort((a, b) => {
      if (b.answer.count !== a.answer.count) {
        return b.answer.count - a.answer.count
      }
      return a.index - b.index
    })
    .map(item => item.answer)
})

const chartData = computed(() => sortedAnswers.value.map(answer => ({ label: answer.label, count: answer.count })))
const chartSize = computed(() => {
  const baseSize = Math.max(360, props.answers.length * 28)
  return Math.min(baseSize, 420)
})

const chartStyles = computed(() => ({
  width: `${chartSize.value}px`,
  height: `${chartSize.value}px`,
  margin: '0'
}))

const chartColors = [
  'rgba(189,189,189,0.8)',
  'rgba(92,107,192,0.8)',
  'rgba(2,119,189,0.8)',
  'rgba(38,166,154,0.8)',
  'rgba(255,167,38,0.8)',
  'rgba(239,83,80,0.8)',
  'rgba(242, 91,141,0.8)',
  'rgba(188,42,141,1)',
  '#F8E91A',
  '#6FA626',
  '#59F0EB',
  '#BC782A',
  '#7D2ABC'
]

const legendItems = computed(() => {
  return sortedAnswers.value.map(answer => {
    const percent = props.totalCount ? Math.round((answer.count / props.totalCount) * Math.pow(100, 2)) / 100 : 0
    return { ...answer, percent }
  })
})

const handleQuestionChange = (value: number) => {
  emit('select-question', value)
}

const resolveLegendColor = (optionId: number) => {
  const index = sortedAnswers.value.findIndex(answer => answer.optionId === optionId)
  return chartColors[index % chartColors.length] ?? chartColors[0]
}

const formatLegendLabel = (item: InstagramInstantwinEnqueteAnswer & { percent: number }) => {
  return `${item.label}：${item.count.toLocaleString()}人(${item.percent}%)`
}

const downloadCsv = () => {
  if (!hasAnswers.value) {
    return { error: 'NO_DATA' }
  }

  const questionText = props.questions.find(question => question.id === props.selectedId)?.text || 'アンケート'
  const normalized = questionText.replace(/[\\/:*?"<>|]/g, '_')
  const filename = `${props.campaignName}_${normalized}`

  const data: Array<Array<string | number | null>> = []
  data.push(['回答', '回答数', '割合'])

  legendItems.value.forEach(item => {
    data.push([item.label, item.count, item.percent])
  })

  return { filename, data }
}
</script>

<style lang="scss" scoped>
@use '@/client/assets/scss/variables.scss' as *;

.enquete-tab {
  width: 100%;
}

.enquete-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: $spacing-unit * 2;
}

.enquete-select {
  width: 320px;
}

.enquete-body {
  display: flex;
  gap: $spacing-unit * 4;
  align-items: flex-start;
}

.enquete-chart {
  flex: 0 0 auto;
  margin-left: $spacing-unit * 11;
}

.enquete-legend {
  display: flex;
  flex-direction: column;
  gap: $spacing-unit;
  padding-top: $spacing-unit;
}

.legend-item {
  display: flex;
  gap: $spacing-unit;
  align-items: center;
}

.legend-color {
  width: 44px;
  height: 12px;
  border-radius: 2px;
}

.legend-text {
  font-size: 16px;
  color: #3f434a;
}
</style>
